import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import type { GoalState } from '@reados/contracts';
import iconPaths from '../vendor/codex-desktop/goal-icon.json';
import clearPaths from '../vendor/codex-desktop/clear-icon.json';
import pausePaths from '../vendor/codex-desktop/pause-icon.json';
import resumePaths from '../vendor/codex-desktop/resume-icon.json';
import expandPaths from '../vendor/codex-desktop/expand-icon.json';
import { formatTokens, goalSummary, isTokenBudget, parseGoalDraft, type GoalSetInput, type GoalStatusAction } from './model';

export interface GoalControlsProps {
  goal: GoalState | null;
  onSet: (input: GoalSetInput) => void | Promise<void>;
  onStatus: (status: GoalStatusAction) => void | Promise<void>;
  onClear: () => void | Promise<void>;
  busy?: boolean;
}

function GoalIcon() {
  return <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    {iconPaths.map((d, i) => <path key={i} d={d} fill="currentColor" fillRule={i === 2 ? 'evenodd' : undefined} clipRule={i === 2 ? 'evenodd' : undefined} />)}
  </svg>;
}

function ActionIcon({ kind }: { kind: 'clear' | 'pause' | 'resume' | 'expand' }) {
  const paths = { clear: clearPaths, pause: pausePaths, resume: resumePaths, expand: expandPaths }[kind];
  return <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    {paths.map((d, index) => <path key={index} d={d}
      fillRule={(kind === 'pause' || kind === 'resume') && index === paths.length - 1 ? 'evenodd' : undefined}
      clipRule={(kind === 'pause' || kind === 'resume') && index === paths.length - 1 ? 'evenodd' : undefined} />)}
  </svg>;
}

/** Desktop jQe/PQe/FQe row + side-panel editor, with host operations injected. */
export function GoalControls(props: GoalControlsProps) {
  // New goals are created by submitting the host composer in goal mode.
  if (!props.goal) return null;
  // New saved objective/budget resets drafts; usage/status updates do not erase edits.
  return <GoalControlsSession key={JSON.stringify([props.goal?.objective ?? null, props.goal?.tokenBudget ?? null])} {...props} />;
}

function GoalControlsSession({ goal, onSet, onStatus, onClear, busy = false }: GoalControlsProps) {
  const id = useId();
  const [editing, setEditing] = useState(false);
  const [objective, setObjective] = useState(goal?.objective ?? '');
  const [budget, setBudget] = useState(isTokenBudget(goal?.tokenBudget) ? String(goal.tokenBudget) : '');
  const [pending, setPending] = useState<'set' | 'status' | 'clear' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now);
  const lock = useRef(false);
  const mounted = useRef(true);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const editButton = useRef<HTMLButtonElement>(null);
  const disabled = busy || pending !== null;
  const summary = goal ? goalSummary(goal, now) : null;
  const parsed = parseGoalDraft(objective, budget, goal);
  const changed = objective.trim() !== (goal?.objective ?? '') || budget.trim() !== (isTokenBudget(goal?.tokenBudget) ? String(goal.tokenBudget) : '');
  const canSave = !disabled && !parsed.error && (goal === null || changed);

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (summary?.status !== 'active') return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [summary?.status]);
  useEffect(() => {
    if (editing) { dialog.current?.showModal(); textarea.current?.focus(); }
    else dialog.current?.close();
  }, [editing]);

  async function run(kind: 'set' | 'status' | 'clear', action: () => void | Promise<void>) {
    if (disabled || lock.current) return;
    lock.current = true;
    setPending(kind);
    setError(null);
    try {
      await action();
      if (mounted.current && kind === 'set') {
        setEditing(false);
        editButton.current?.focus();
      }
    } catch (cause) {
      if (mounted.current) setError(cause instanceof Error && cause.message ? cause.message : '目标操作失败，请重试。');
    } finally {
      lock.current = false;
      if (mounted.current) setPending(null);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!parsed.input) { setError(parsed.error); return; }
    const input = parsed.input;
    if (canSave) void run('set', () => onSet(input));
  }

  function revert() {
    setObjective(goal?.objective ?? '');
    setBudget(isTokenBudget(goal?.tokenBudget) ? String(goal.tokenBudget) : '');
    setError(null);
    textarea.current?.focus();
  }

  function closeEditor() {
    if (disabled) return;
    revert();
    setEditing(false);
    editButton.current?.focus();
  }

  function shortcut(event: KeyboardEvent<HTMLFormElement>) {
    if (event.nativeEvent.isComposing || event.repeat) return;
    // Desktop fQe + MQe: Cmd/Ctrl+S and Cmd/Ctrl+Enter save the editor.
    if ((event.metaKey || event.ctrlKey) && (event.key.toLowerCase() === 's' || event.key === 'Enter')) {
      event.preventDefault();
      event.stopPropagation();
      if (canSave) event.currentTarget.requestSubmit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeEditor();
    }
  }

  return <section className="reados-goal-controls" aria-label="目标模式" aria-busy={disabled} data-has-goal={goal !== null}>
    {goal && summary ? <>
      <div className="goal-row">
        <span className="goal-icon"><GoalIcon /></span>
        <button className="goal-summary" type="button" disabled={disabled}
          aria-label={`编辑目标：${goal.objective}`} aria-expanded={editing} aria-controls={`${id}-editor`}
          onClick={() => { setEditing(true); setError(null); }}>
          <span className="goal-status text-default" data-status={summary.status}>{summary.label}</span>
          <span className="goal-objective truncate-text text-codex-description" title={goal.objective}>{goal.objective}</span>
          <span className="goal-compact whitespace-nowrap text-codex-description tabular-nums" aria-label={`目标用量：${summary.compact}`}>{summary.compact}</span>
        </button>
        <div className="goal-actions" aria-label="目标操作">
          <button type="button" disabled={disabled} aria-label="清除目标" title="清除目标" onClick={() => void run('clear', onClear)}>
            <ActionIcon kind="clear" />
          </button>
          {summary.action && <button type="button" disabled={disabled}
            aria-label={summary.action === 'paused' ? '暂停目标' : '恢复目标'} title={summary.action === 'paused' ? '暂停目标' : '恢复目标'}
            onClick={() => void run('status', () => onStatus(summary.action!))}>
            <ActionIcon kind={summary.action === 'paused' ? 'pause' : 'resume'} />
          </button>}
          <button ref={editButton} type="button" disabled={disabled} aria-label="展开目标" title="展开目标"
            aria-expanded={editing} aria-controls={`${id}-editor`} onClick={() => { setEditing(true); setError(null); }}>
            <ActionIcon kind="expand" />
          </button>
        </div>
      </div>
    </> : <button ref={editButton} type="button" className="goal-create" disabled={disabled}
      aria-expanded={editing} aria-controls={`${id}-editor`} onClick={() => { setEditing(!editing); setError(null); }}>
      <GoalIcon />设置目标
    </button>}

    <dialog ref={dialog} id={`${id}-editor`} className="goal-dialog" aria-labelledby={`${id}-editor-title`}
      onCancel={event => { event.preventDefault(); closeEditor(); }}>
    {editing && <form className="goal-editor bg-surface" onSubmit={submit} onKeyDown={shortcut}>
      <div className="goal-editor-heading">
        <strong id={`${id}-editor-title`}>{goal ? '编辑目标' : '设置目标'}</strong>
        <button type="button" disabled={disabled} onClick={closeEditor} aria-label="关闭目标面板">关闭</button>
      </div>
      {summary && <div className="goal-metrics text-codex-description tabular-nums" aria-label="目标使用情况">
        <span>{summary.label}</span>
        <span>已用 {summary.used === null ? '待同步' : `${formatTokens(summary.used)} Token`}</span>
        <span>预算 {summary.budget === null ? '未设置' : `${formatTokens(summary.budget)} Token`}</span>
        {summary.remaining !== null && <span>{summary.overBudget! > 0 ? `已超出 ${formatTokens(summary.overBudget!)} Token` : `剩余 ${formatTokens(summary.remaining)} Token`}</span>}
        <span>累计用时 {summary.elapsedText}</span>
      </div>}
      {summary?.percentage != null && <progress className="goal-progress" max="100" value={summary.percentage} aria-label="目标 Token 预算使用比例" />}
      <label htmlFor={`${id}-objective`}>目标</label>
      <textarea ref={textarea} id={`${id}-objective`} rows={5} value={objective} disabled={disabled}
        placeholder="描述希望完成的工作，以及完成的标准…" required
        onChange={event => { setObjective(event.target.value); setError(null); }} />
      <label htmlFor={`${id}-budget`}>Token 预算 <span className="goal-optional">（可选）</span></label>
      <input id={`${id}-budget`} type="text" inputMode="numeric" autoComplete="off" value={budget} disabled={disabled}
        aria-describedby={`${id}-budget-hint`} placeholder="留空表示不设置预算"
        onChange={event => { setBudget(event.target.value); setError(null); }} />
      <p id={`${id}-budget-hint`} className="goal-hint">达到预算后，目标会停止推进。{parsed.error && objective.trim() ? ` ${parsed.error}` : ''}</p>
      <div className="goal-editor-actions">
        <button type="button" disabled={disabled || !changed} onClick={revert}>还原</button>
        <span className="goal-action-spacer" />
        <button type="button" disabled={disabled} onClick={closeEditor}>取消</button>
        <button type="submit" className="goal-primary" disabled={!canSave}>{pending === 'set' ? '保存中…' : goal ? '保存目标' : '开始目标'}</button>
      </div>
      {error && <p className="goal-error" role="alert">{error}</p>}
    </form>}
    </dialog>
    {!editing && error && <p className="goal-error" role="alert">{error}</p>}
  </section>;
}
